<template>
  <div>
    <navbar :links="links" />
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 mb-4 msr--text"
      />
    </div>
    <!-- Campaign Detail -->
    <v-list
      v-if="campaign"
      dense
      two-line
    >
      <v-list-tile
        avatar
        class="no-hover-clr no-click-pointer"
        :ripple="false"
      >
        <!-- Unpublished Icon -->
        <v-avatar
          v-if="!campaign.published"
          :title="$t('campaignNotPublished')"
        >
          <v-icon color="msr">
            visibility_off
          </v-icon>
        </v-avatar>
        <v-list-tile-avatar v-else>
          <v-icon
            large
            class="msr--text"
          >
            flag
          </v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>
            <v-layout
              row
              wrap
            >
              <strong class="grey--text text--darken-3 mr-2">{{
                campaign.title
              }}</strong>
              <v-spacer />
              <div class="grey--text">
                <span v-if="campaign.num_tv_spot_bundles">
                  {{ campaign.num_tv_spot_bundles }} x
                  <v-icon class="sub-icon mr-1">tv</v-icon>
                </span>
                <span v-if="campaign.num_radio_spots">
                  {{ campaign.num_radio_spots }} x
                  <v-icon class="sub-icon mr-1">music_note</v-icon>
                </span>
              </div>
            </v-layout>
          </v-list-tile-title>
          <v-list-tile-sub-title>
            <div
              v-if="campaign.expiry_date"
              class="warning--text"
            >
              {{ $t('expiresOn') }}
              {{
                subtractOneDayFromDate(campaign.expiry_date)
                  | dayjs('DD.MM.YYYY')
              }}
            </div>
          </v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
    <div v-if="campaign">
      <!-- Poster -->
      <v-card
        flat
        class="mt-1 mb-1"
      >
        <v-img
          v-if="campaign.poster"
          :height="$vuetify.breakpoint.xsOnly ? 95 : 240"
          style="max-width: 800px"
          contain
          :src="campaign.poster"
        />
      </v-card>
      <!-- Description -->
      <v-card
        v-if="campaign.description"
        flat
      >
        <v-card-title class="grey--text text--darken-2 pl-0 pr-0">
          {{ campaign.description }}
        </v-card-title>
      </v-card>
      <!-- Content Manager Actions -->
      <v-layout
        v-if="
          (campaign.published && permissions.unpublish) || permissions.delete
        "
        column
        class="mt-3 mb-2"
      >
        <v-layout align-center>
          <v-menu
            offset-y
            left
            class="ml-auto"
          >
            <v-btn
              slot="activator"
              icon
              class="ma-0 grey--text text--darken-1"
              data-cy="content-actions-activator"
            >
              <v-icon
                class="hoverable-icon"
                medium
              >
                settings
              </v-icon>
            </v-btn>
            <v-list>
              <!-- Publish -->
              <v-list-tile
                v-if="
                  !campaign.published && campaignDetail.user_can_manage_content
                "
                @click="showPublishDialog = true"
              >
                <v-list-tile-avatar>
                  <v-icon class="msr--text">
                    visibility
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ $t('publishCampaign') }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
              <!-- Unpublish -->
              <v-list-tile
                v-if="
                  campaign.published && campaignDetail.user_can_manage_content
                "
                data-cy="unpublish-btn"
                @click="showUnpublishDialog = true"
              >
                <v-list-tile-avatar>
                  <v-icon class="msr--text">
                    visibility_off
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ $t('unpublishCampaign') }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
              <!-- Delete -->
              <v-list-tile
                v-if="permissions.delete"
                @click="showDeletionDialog = true"
              >
                <v-list-tile-avatar>
                  <v-icon class="msr--text">
                    delete
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ $t('deleteCampaign') }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>
      </v-layout>
      <!-- Unpublished, publish now? -->
      <v-list v-if="!campaign.published">
        <v-list-tile :class="{'mt-2': $vuetify.breakpoint.xsOnly}">
          <unpublished-indicator
            is-unpublished
            :use-default-margins="false"
          />
          <v-subheader>
            <v-layout
              row
              wrap
            >
              <div class="mr-1">
                {{ $t('campaignNotPublished') }} -
                <a
                  href="javascript:void(0)"
                  data-cy="anchor-publish-content-now"
                  @click="() => (showPublishDialog = true)"
                >{{ $t('publishContentNow') }}</a>
              </div>
            </v-layout>
          </v-subheader>
        </v-list-tile>
      </v-list>
      <v-list
        v-if="!loading && campaign.published"
        class="list-tile-auto-height"
      >
        <!-- Show hint about activation -->
        <v-list-tile
          v-if="notYetActivatedAnywhere"
          class="my-2"
        >
          <v-icon
            v-if="
              timeframeChosen &&
                atLeastOneContentSelected &&
                atLeastOneStoreSelected
            "
            medium
            color="info"
          >
            info
          </v-icon>
          <v-icon
            v-else
            medium
            color="warning"
          >
            warning
          </v-icon>
          <v-subheader
            class="auto-height"
            :class="[
              timeframeChosen &&
                atLeastOneContentSelected &&
                atLeastOneStoreSelected
                ? 'info--text'
                : 'warning--text',
            ]"
          >
            {{ $t('campaignEnableHelp') }}
          </v-subheader>
        </v-list-tile>
        <!-- Contains editable Content -->
        <v-list-tile
          v-if="campaign.contains_editable_content"
          class="my-2"
        >
          <v-icon
            medium
            color="msr"
          >
            edit
          </v-icon>
          <v-subheader class="auto-height">
            {{ $t('campaignContainsEditableContent') }}
          </v-subheader>
        </v-list-tile>
      </v-list>
      <!-- Partial/full activation status and deactivation button -->
      <campaign-activation-status
        :campaign="campaign"
        :loading="loading"
        :activated-everywhere="activatedEverywhere"
        :activated-in-some-stores="activatedInSomeStores"
        :deactivating="deactivating"
        @deactivate="deactivateCampaign()"
      />
      <!-- Options -->
      <div
        v-if="campaign.published && !activatedEverywhere"
        class="mt-4"
      >
        <h4 class="title mb-2">
          {{ $t('setTiming') }}
        </h4>
        <v-layout
          wrap
          class="mb-2"
        >
          <!-- Startdate -->
          <div class="menu-container">
            <v-menu
              v-model="startDateMenu"
              :disabled="activating || deactivating"
              class="mr-2"
              lazy
              offset-y
              min-width="290"
              :nudge-left="40"
            >
              <v-text-field
                slot="activator"
                v-model="startDate"
                :label="$t('startdate')"
                :rules="[rules.startdate]"
                prepend-icon="event"
                readonly
              />
              <v-date-picker
                v-model="startDate"
                width="290"
                color="msr"
                scrollable
                event-color="msr"
                :events="isEventForChosenDateRange"
                :no-title="$vuetify.breakpoint.xsOnly"
                :first-day-of-week="1"
                :locale="$t('_datepickerLocale')"
                :allowed-dates="isNotInPast"
              />
            </v-menu>
          </div>
          <!-- Stopdate -->
          <div class="menu-container">
            <v-menu
              v-model="stopDateMenu"
              lazy
              offset-y
              min-width="290"
              :disabled="activating || deactivating"
              :nudge-left="40"
            >
              <v-text-field
                slot="activator"
                v-model="stopDate"
                :label="$t('stopdate')"
                :rules="[rules.stopdate]"
                prepend-icon="event"
                readonly
              />
              <v-date-picker
                v-model="stopDate"
                width="290"
                color="msr"
                scrollable
                event-color="msr"
                :events="isEventForChosenDateRange"
                :no-title="$vuetify.breakpoint.xsOnly"
                :first-day-of-week="1"
                :locale="$t('_datepickerLocale')"
                :allowed-dates="isNotInPast"
              />
            </v-menu>
            <v-btn
              v-if="stopDate"
              flat
              icon
              :disabled="activating || deactivating"
              @click="stopDate = ''"
            >
              <v-icon class="msr--text">
                clear
              </v-icon>
            </v-btn>
          </div>
        </v-layout>
      </div>
      <!-- Note: Clicking a Spot anywhere except the preview should
			     toggle the checkbox. We need to disable the checkbox click
			     handler to avoid double triggering and configure the spot
			     component accordingly.
      -->
      <!-- TV Clips -->
      <detail-entries-header
        v-if="campaign && validCampaignTVSpotBundles.length"
        data-cy="tv-entries-header"
        show-expander
        :prefix1="$t('tvContent')"
        :num1="numTVContentsForHeader"
        :all-expanded.sync="tvSpotBundlesExpanded"
      />
      <v-slide-y-transition>
        <div
          v-if="tvSpotBundlesExpanded"
          :key="lastFetched"
        >
          <tv-spot-bundle
            v-for="(bundle, bundleIndex) in validCampaignTVSpotBundles"
            :key="bundle.id"
            :bundle="bundle"
            :highlight-title="
              activatedEverywhere ? false : tvSpotBundleIdToSelected[bundle.id]
            "
            :show-new-badge="false"
            :show-tvformat-names="bundle.has_multiple_formats"
            :show-divider="
              bundleIndex !== validCampaignTVSpotBundles.length - 1
            "
            :on-click="() => toggleTvSpotBundleSelected(bundle)"
          >
            <div
              v-if="
                campaign.published &&
                  getMainTVSpotFromBundle(bundle).published &&
                  !activatedEverywhere &&
                  !loading &&
                  !activating &&
                  !deactivating
              "
              slot="actions"
            >
              <!--
						  Checkbox click handler disabled so we can handle it on
						  its parent component instead.
              -->
              <v-checkbox
                v-model="tvSpotBundleIdToSelected[bundle.id]"
                class="msr--text ml-2 my-auto"
                color="msr"
                hide-details
                @click.prevent
              />
            </div>
          </tv-spot-bundle>
        </div>
      </v-slide-y-transition>
      <!-- Radio Spots -->
      <detail-entries-header
        v-if="campaign && validCampaignRadioSpots.length"
        show-expander
        data-cy="radio-entries-header"
        :prefix1="$t('radioContent')"
        :num1="numRadioContentsForHeader"
        :all-expanded.sync="radioSpotsExpanded"
      />
      <v-slide-y-transition>
        <div
          v-if="radioSpotsExpanded"
          :key="lastFetched"
        >
          <spot
            v-for="(spot, spotIndex) in validCampaignRadioSpots"
            :key="spot.id"
            :disabled="activating || deactivating"
            :spot="spot"
            :ctype="spot.ctype"
            :on-click="
              () =>
                activatedEverywhere ||
                activating ||
                deactivating ||
                !campaign.published
                  ? null
                  : (radioSpotIdToSelected[spot.id] =
                    !radioSpotIdToSelected[spot.id])
            "
            :highlight-title="
              activatedEverywhere ? false : radioSpotIdToSelected[spot.id]
            "
            :show-new-badge="false"
            :show-divider="spotIndex !== campaign.radio_spots.length - 1"
          >
            <div
              v-if="
                campaign.published &&
                  spot.published &&
                  !activatedEverywhere &&
                  !loading &&
                  !activating &&
                  !deactivating
              "
              slot="actions"
            >
              <v-checkbox
                v-model="radioSpotIdToSelected[spot.id]"
                class="msr--text ml-2 my-auto"
                color="msr"
                hide-details
                @click="() => null"
              />
            </div>
          </spot>
        </div>
      </v-slide-y-transition>
      <!-- No Stores available -->
      <no-entries-header
        content-class="bg-warning"
        :show="campaign && availableStores.length === 0"
        :label="$t('noStoresForCampaign')"
        :show-activation-link="false"
      />
      <!-- Stores -->
      <detail-entries-header
        v-if="campaign && availableStores.length"
        :prefix1="$t('stores')"
        :num1="numStoresForHeader"
        show-expander
        :all-expanded.sync="storesExpanded"
        data-cy="stores-header"
      />
      <v-slide-y-transition>
        <div
          v-if="storesExpanded"
          :key="lastFetched"
        >
          <v-list
            dense
            subheader
          >
            <store-tile
              v-for="(store, storeIndex) in availableStores"
              :key="store.id"
              :store="store"
              :show-divider="storeIndex !== availableStores.length - 1"
              :highlight-title="
                !isCampaignActivatedForStore(store)
                  ? storeIdToSelected[store.id]
                  : false
              "
              @click="
                () =>
                  isCampaignActivatedForStore(store) ||
                  activatedEverywhere ||
                  activating ||
                  deactivating ||
                  !campaign.published
                    ? null
                    : (storeIdToSelected[store.id] =
                      !storeIdToSelected[store.id])
              "
            >
              <div slot="actions">
                <span v-if="campaign.published">
                  <v-checkbox
                    v-if="
                      !activatedEverywhere &&
                        !loading &&
                        !activating &&
                        !deactivating &&
                        !isCampaignActivatedForStore(store)
                    "
                    v-model="storeIdToSelected[store.id]"
                    class="msr--text"
                    color="msr"
                    @click="() => null"
                  />
                  <div v-else>
                    <!-- Campaign is already active on this Store -->
                    <v-icon class="success--text"> check </v-icon>
                  </div>
                </span>
                <span v-else><!-- Empty --></span>
              </div>
            </store-tile>
          </v-list>
        </div>
      </v-slide-y-transition>
    </div>
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 100px" />
    <!-- Campaign Activation -->
    <v-snackbar
      :value="showActivationSnackbar"
      :timeout="0"
      multi-line
      :class="$desktopModeStyles"
      color="grey lighten-4"
      class="fixed-snackbar-z-index larger-v-switch"
    >
      <!-- Note: v-switch and other selection controls have no relative
		       context and as such their size has no effect on positioning
		       and we need to trick a bit to 'center' it here.
      -->
      <!-- Enable Campaign Switch -->
      <v-switch
        v-model="campaignEnabled"
        class="mt-3 mb-3"
        color="msr"
        :class="{
          'ml-3': $vuetify.breakpoint.smAndUp,
          'center-aligned-switch': $vuetify.breakpoint.xsOnly,
          'v-label--msr--text':
            atLeastOneContentSelected &&
            atLeastOneStoreSelected &&
            timeframeChosen,
        }"
        :disabled="
          !atLeastOneContentSelected ||
            !atLeastOneStoreSelected ||
            !timeframeChosen
        "
        :label="$t('campaignEnableHint')"
        data-cy="activate-campaign-switch"
        hide-details
      />
      <div class="loading-indicator">
        <v-progress-circular
          v-if="activating"
          style="position: relative"
          :style="{right: $vuetify.breakpoint.smAndUp ? '0' : '50px'}"
          :size="30"
          indeterminate
          class="msr--text ml-2"
        />
      </div>
    </v-snackbar>
    <!-- Configure Campaign's TVSpot OverlayData Dialog -->
    <campaign-overlay-data-dialog
      v-if="campaign"
      :show.sync="showCampaignOverlayDataDialog"
      :selected-editable-tv-spot-bundles="selectedEditableTVSpotBundles"
      @configured="onCampaignOverlayDataConfigured"
    />
    <!-- Publish Campaign Dialog -->
    <generic-confirmation-dialog
      v-if="campaign && !campaign.published && permissions.publish"
      :show.sync="showPublishDialog"
      :loading="publishing"
      @confirmed="onPublishCampaignConfirmed(loadInitialPage)"
    >
      <h1
        slot="title"
        class="headline full-width"
      >
        <v-layout align-center>
          <v-icon
            large
            class="msr--text mr-3"
          >
            visibility
          </v-icon>
          <span class="text-xs-center">{{ $t('publishCampaign') }}</span>
        </v-layout>
      </h1>
      <div
        slot="content"
        class="subheading"
      >
        <div class="mb-2">
          <strong>{{ campaign.title }}</strong>
        </div>
        {{ $t('publishCampaignHint') }}
        <div
          v-if="hasUnpublishedSpots"
          class="mt-2 px-2 py-1 warning--text"
        >
          <v-layout
            v-if="numUnpublishedRadioSpots > 0"
            align-center
            class="my-1"
          >
            <v-icon
              color="warning"
              class="mr-2"
            >
              music_note
            </v-icon>
            {{ $t('publishCampaignAlongsideHint') }}:
            {{ numUnpublishedRadioSpots }}
            {{ $t('radioContent') }}
          </v-layout>
          <v-layout
            v-if="numUnpublishedTVSpots > 0"
            align-center
            class="my-1"
          >
            <v-icon
              color="warning"
              class="mr-2"
            >
              tv
            </v-icon>
            {{ $t('publishCampaignAlongsideHint') }}:
            {{ numUnpublishedTVSpots }}
            {{ $t('tvContent') }}
          </v-layout>
        </div>
      </div>
    </generic-confirmation-dialog>
    <!-- Unpublish Campaign Dialog (and possibly its contents) -->
    <generic-confirmation-dialog
      v-if="campaign && campaign.published && permissions.unpublish"
      :show.sync="showUnpublishDialog"
      :loading="publishing"
      @confirmed="onUnpublishCampaignConfirmed(loadInitialPage)"
    >
      <h1
        slot="title"
        class="headline"
      >
        <v-layout
          row
          align-center
          justify-center
          wrap
        >
          <v-icon
            large
            class="msr--text mr-3"
          >
            visibility_off
          </v-icon>
          <span class="text-xs-center">{{ $t('unpublishCampaign') }}</span>
        </v-layout>
      </h1>
      <div
        slot="content"
        class="subheading"
      >
        <div class="mb-2">
          <strong>{{ campaign.title }}</strong>
        </div>
        <div>{{ $t('unpublishCampaignHint') }}</div>
        <v-checkbox
          v-if="hasPublishedSpots"
          v-model="unpublishCampaignContentsAlso"
          :label="$t('unpublishCampaignContents')"
          color="msr"
          class="mb-3"
          persistent-hint
          :hint="unpublishCampaignContentsAlsoHint"
        />
        <v-slide-y-reverse-transition>
          <div
            v-if="unpublishCampaignContentsAlso"
            class="pa-2 error--text"
          >
            <strong>{{ $t('unpublishContentHint_2') }}</strong>
          </div>
        </v-slide-y-reverse-transition>
      </div>
    </generic-confirmation-dialog>
    <!-- Delete Campaign Dialog (and possibly its contents) -->
    <generic-confirmation-dialog
      v-if="campaign && permissions.delete"
      :show.sync="showDeletionDialog"
      :loading="loading"
      @confirmed="onDeleteCampaignConfirmed()"
    >
      <h1
        slot="title"
        class="headline"
      >
        <v-layout
          row
          align-center
          justify-center
          wrap
        >
          <v-icon
            large
            class="msr--text mr-3"
          >
            delete
          </v-icon>
          <span class="text-xs-center">{{ $t('deleteCampaign') }}</span>
        </v-layout>
      </h1>
      <div
        slot="content"
        class="subheading"
      >
        <div class="mb-2">
          <strong>{{ campaign.title }}</strong>
        </div>
        <div class="pa-2 error--text">
          <strong>{{ $t('deleteCampaignHint') }}</strong>
        </div>
        <v-checkbox
          v-model="deleteCampaignContentsAlso"
          :label="$t('deleteCampaignContents')"
          color="msr"
          class="mb-3"
          persistent-hint
          :hint="deleteCampaignContentsAlsoHint"
        />
        <v-slide-y-reverse-transition>
          <div
            v-if="deleteCampaignContentsAlso"
            class="pa-2 error--text"
          >
            <strong>{{ $t('deleteCampaignContentDeactivatesHint') }}</strong>
          </div>
        </v-slide-y-reverse-transition>
      </div>
    </generic-confirmation-dialog>
  </div>
</template>

<script>
import {getMainTVSpotFromBundle} from '@/utils';
import campaignDeletion from '@/mixins/campaignDeletion.js';
import campaignPublishing from '@/mixins/campaignPublishing.js';
import campaigns from '@/mixins/campaigns';
import timing from '@/mixins/timing';

import CampaignActivationStatus from '@/components/CampaignActivationStatus';
import CampaignOverlayDataDialog from '@/components/CampaignOverlayDataDialog';
import DetailEntriesHeader from '@/components/DetailEntriesHeader';
import GenericConfirmationDialog from '@/components/GenericConfirmationDialog';
import Navbar from '@/components/Navbar';
import NoEntriesHeader from '@/components/NoEntriesHeader';
import Spot from '@/components/Spot';
import StoreTile from '@/components/StoreTile';
import TvSpotBundle from '@/components/TvSpotBundle';
import UnpublishedIndicator from '@/components/UnpublishedIndicator';

export default {
  components: {
    CampaignActivationStatus,
    CampaignOverlayDataDialog,
    DetailEntriesHeader,
    GenericConfirmationDialog,
    Navbar,
    NoEntriesHeader,
    Spot,
    StoreTile,
    TvSpotBundle,
    UnpublishedIndicator,
  },
  mixins: [campaignDeletion, campaignPublishing, campaigns, timing],
  data() {
    return {
      links: ['dashboard', 'campaigns'],
      loading: false,
      activating: false,
      deactivating: false,
      showCampaignOverlayDataDialog: false,
      lastFetched: null,

      campaignDetail: {
        campaign: null,
        usercampaign: null,
      },

      campaignEnabled: false,

      rules: {
        startdate: (date) => {
          return !!date || this.$t('campaignActivationRequiresStartDate');
        },
        stopdate: (date) => {
          return !!date || this.$t('campaignActivationRequiresStopDate');
        },
      },

      // Track which things the user has selected by mapping ids
      // to Booleans as in: '1234: true'.
      storeIdToSelected: {},
      tvSpotBundleIdToSelected: {},
      radioSpotIdToSelected: {},

      startDateMenu: false,
      stopDateMenu: false,

      storesExpanded: false,
      tvSpotBundlesExpanded: false,
      radioSpotsExpanded: false,
    };
  },
  computed: {
    validCampaignTVSpotBundles() {
      if (this.isGroupContentManager) {
        return this.campaign.tv_spot_bundles;
      }
      return this.campaign.tv_spot_bundles.filter((bundle) => {
        const mainTVSpot = getMainTVSpotFromBundle(bundle);
        return mainTVSpot.published;
      });
    },
    validCampaignRadioSpots() {
      let visibleRadioSpots = this.campaign.radio_spots;
      if (!this.isGroupContentManager) {
        visibleRadioSpots = this.campaign.radio_spots.filter(
          (spot) => spot.published
        );
      }
      return visibleRadioSpots;
    },
    isGroupContentManager() {
      const uploadGroupIds = this.$store.getters.uploadgroups.map(
        (group) => group.id
      );
      const isGroupContentManager =
        uploadGroupIds.indexOf(this.campaign.group.id) !== -1;
      return isGroupContentManager;
    },
    permissions() {
      if (!this.campaign || !this.$user) {
        return {};
      }
      return {
        publish: this.isGroupContentManager,
        unpublish: this.isGroupContentManager,
        delete: this.isGroupContentManager,
      };
    },
    availableStores() {
      return this.availableStoresForCampaign(this.campaign);
    },
    campaignId() {
      return this.$route.params.campaignId;
    },
    campaign() {
      return this.campaignDetail.campaign;
    },
    notYetActivatedAnywhere() {
      if (
        !this.campaign ||
        !this.campaign.activations ||
        !this.availableStores.length
      ) {
        return true;
      }
      return this.campaign.activations.length === 0;
    },
    activatedEverywhere() {
      if (
        !this.campaign ||
        !this.campaign.activations ||
        !this.availableStores.length
      ) {
        return false;
      }
      return this.campaign.activations.length === this.availableStores.length;
    },
    activatedInSomeStores() {
      if (
        !this.campaign ||
        !this.campaign.activations ||
        !this.availableStores.length
      ) {
        return false;
      }
      return (
        this.campaign.activations.length > 0 &&
        this.campaign.activations.length < this.availableStores.length
      );
    },
    selectedStoreIds() {
      return Object.keys(this.storeIdToSelected).filter((key) => {
        const value = this.storeIdToSelected[key];
        return value !== false;
      });
    },
    selectedTVSpotBundleIds() {
      return Object.keys(this.tvSpotBundleIdToSelected).filter((key) => {
        const value = this.tvSpotBundleIdToSelected[key];
        return value !== false;
      });
    },
    selectedEditableTVSpotBundles() {
      return this.campaign.tv_spot_bundles.filter((bundle) => {
        const id = String(bundle.id);
        const chosen = this.selectedTVSpotBundleIds.indexOf(id) !== -1;
        const mainTVSpot = getMainTVSpotFromBundle(bundle);
        return mainTVSpot.is_editable && chosen;
      });
    },
    selectedRadioSpotIds() {
      return Object.keys(this.radioSpotIdToSelected).filter((key) => {
        const value = this.radioSpotIdToSelected[key];
        return value !== false;
      });
    },
    atLeastOneContentSelected() {
      return (
        this.selectedTVSpotBundleIds.length + this.selectedRadioSpotIds.length >
        0
      );
    },
    atLeastOneStoreSelected() {
      return this.selectedStoreIds.length > 0;
    },
    allContentsSelected() {
      if (!this.campaign) {
        return true;
      }
      const allTVSpotBundlesSelected =
        this.selectedTVSpotBundleIds.length ===
        this.campaign.tv_spot_bundles.length;
      const allRadioSpotsSelected =
        this.selectedRadioSpotIds.length === this.campaign.radio_spots.length;
      return allTVSpotBundlesSelected && allRadioSpotsSelected;
    },
    timeframeChosen() {
      return this.startDate && this.stopDate;
    },
    showActivationSnackbar() {
      return (
        !this.deactivating &&
        !this.activatedEverywhere &&
        this.campaign &&
        this.campaign.published
      );
    },
    numRadioContentsForHeader() {
      if (this.activatedEverywhere) {
        return this.validCampaignRadioSpots.length;
      }
      return (
        this.selectedRadioSpotIds.length +
        '/' +
        this.validCampaignRadioSpots.length
      );
    },
    numTVContentsForHeader() {
      if (this.activatedEverywhere) {
        return this.validCampaignTVSpotBundles.length;
      }
      return (
        this.selectedTVSpotBundleIds.length +
        '/' +
        this.validCampaignTVSpotBundles.length
      );
    },
    numStoresForHeader() {
      if (this.activatedEverywhere) {
        return this.availableStores.length;
      }
      // If we already activated a Store, count that as 'selected', too.
      return (
        this.selectedStoreIds.length +
        this.campaign.activations.length +
        '/' +
        this.availableStores.length
      );
    },
    activationsStoreIds() {
      return this.campaign.activations.map((activation) => activation.store);
    },
  },
  watch: {
    /**
     * If the default selection has changed, make the User confirm navigation.
     *
     * Does nothing if the Campaign has already been activated.
     */
    allContentsSelected: {
      deep: true,
      handler: function (allSelected) {
        let isDefault = allSelected === true;
        if (!isDefault && this.activatedEverywhere) {
          isDefault = true;
        }

        if (!isDefault) {
          this.$store.commit(
            'SET_PRE_ROUTE_QUESTION',
            this.$t('selectionChangedRouteConfirm')
          );
        } else {
          this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
        }
      },
    },
    /**
     * Activate the campaign when the v-switch is triggered.
     */
    campaignEnabled(enabled) {
      if (!enabled) {
        return;
      }
      if (this.activating) {
        return;
      }

      if (
        this.campaign.contains_editable_content &&
        this.selectedEditableTVSpotBundles.length > 0
      ) {
        this.showCampaignOverlayDataDialog = true;
        this.$nextTick(() => {
          this.campaignEnabled = false;
        });
        return;
      }

      const confirmed = confirm(this.$t('campaignAskConfirmActivation'));
      if (!confirmed) {
        this.$nextTick(() => {
          this.campaignEnabled = false;
        });
        return;
      }

      this.activateCampaign();
    },
  },
  mounted() {
    this.loadInitialPage();
  },
  methods: {
    /** Fetch Campaign and and Stores and preselect some things. */
    loadInitialPage() {
      this.loading = true;

      let campaignPromise = this.fetchCampaignDetail(this.campaignId);
      let storesPromise = this.$store.dispatch('LIST_USER_WRITABLE_STORES');

      return Promise.all([campaignPromise, storesPromise]).then(() => {
        this.loading = false;
        this.setInitialDates();
        this.preselectAllContents();
      });
    },
    toggleTvSpotBundleSelected(bundle) {
      if (
        this.activatedEverywhere ||
        this.activating ||
        this.deactivating ||
        !this.campaign.published
      ) {
        return;
      }

      // Toggle selection.
      this.tvSpotBundleIdToSelected[bundle.id] =
        !this.tvSpotBundleIdToSelected[bundle.id];
    },
    /* Local method marshalling for use in template. */
    getMainTVSpotFromBundle(bundle) {
      return getMainTVSpotFromBundle(bundle);
    },
    isCampaignActivatedForStore(store) {
      return this.activationsStoreIds.indexOf(store.id) !== -1;
    },
    onCampaignOverlayDataConfigured(spotIdToOverlayData) {
      this.activateCampaign(spotIdToOverlayData);
    },
    deactivateCampaign() {
      const confirmed = confirm(this.$t('campaignDeactivateEverywhereConfirm'));
      if (!confirmed) {
        return;
      }

      this.deactivating = true;
      this.$api
        .deactivateCampaign(this.campaignId)
        .then(() => {
          this.loadInitialPage().then(() => {
            this.$bus.$emit('alert-success', {
              message: this.$t('campaignDeactivatedSuccess'),
              translate: true,
            });
          });
        })
        .finally(() => {
          this.deactivating = false;
        });
    },
    activateCampaign(spotIdToOverlayData = null) {
      const payload = {
        radio_spot_ids: this.selectedRadioSpotIds.join(','),
        startdate: this.startDate,
        stopdate: this.stopDate,
        store_ids: this.selectedStoreIds.join(','),
        tv_spot_bundle_ids: this.selectedTVSpotBundleIds.join(','),
      };
      if (spotIdToOverlayData) {
        payload['spot_id_to_overlay_data'] = spotIdToOverlayData;
      }

      this.activating = true;
      this.$api.activateCampaign(this.campaignId, payload).then(() => {
        this.$bus.$emit('alert-success', {
          message: 'campaignEnableSuccess',
          translate: true,
          timeout: 5000,
        });

        this.fetchCampaignDetail(this.campaignId).then(() => {
          this.activating = false;

          this.$nextTick(() => {
            this.campaignEnabled = false;
          });

          this.deselectAllStores();
        });
      });
    },
    updateNavbarLinks() {
      let lastLink = {
        label: this.campaign.title,
        active: false,
      };
      this.links = ['dashboard', 'campaigns', lastLink];
    },
    /**
     * Deselect all Stores.
     */
    deselectAllStores() {
      this.$set(this, 'storeIdToSelected', {});
      this.availableStores.forEach((store) => {
        this.$set(this.storeIdToSelected, store.id, false);
      });
    },
    /**
     * Select all contents at the start.
     *
     * Unless they are not published or not approved, in which case
     * they will be deselected and their widgets be hidden.
     *
     * We need to reset the dicts first and use $set after, otherwise
     * Vue won't detect any changes to those values through the UI
     * anymore.
     */
    preselectAllContents(workaroundCheckboxesNotRerendering = true) {
      const vm = this;

      this.$set(this, 'tvSpotBundleIdToSelected', {});
      this.campaign.tv_spot_bundles.forEach((bundle) => {
        const mainTVSpot = getMainTVSpotFromBundle(bundle);
        this.$set(
          this.tvSpotBundleIdToSelected,
          bundle.id,
          mainTVSpot.published
        );
      });

      this.$set(this, 'radioSpotIdToSelected', {});
      this.campaign.radio_spots.forEach((spot) => {
        this.$set(this.radioSpotIdToSelected, spot.id, spot.published);
      });

      // Do only select Stores that have not yet been activated.
      this.$set(this, 'storeIdToSelected', {});
      this.availableStores.forEach((store) => {
        const matchingActivations = vm.campaign.activations.filter(
          (activation) => activation.store == store.id
        );
        const alreadyActivated = matchingActivations.length > 0;
        if (alreadyActivated) {
          return;
        }
        this.$set(this.storeIdToSelected, store.id, true);
      });

      if (workaroundCheckboxesNotRerendering) {
        // Publishing a newly created Campaign will not properly update
        // the e.g. TV checkbox states for some reason that I haven't
        // figured out yet. As a workaround we toggle each expandable
        // section twice to force a rerender.
        const expandables = [
          'storesExpanded',
          'tvSpotBundlesExpanded',
          'radioSpotsExpanded',
        ];
        for (const attr of expandables) {
          this[attr] = !this[attr];
        }
        this.$nextTick(() => {
          for (const attr of expandables) {
            this[attr] = !this[attr];
          }
        });
      }
    },
    normalizeDateForPicker(dateString, fixStopDate = false) {
      if (dateString === null) {
        return '';
      }
      let date = new Date(dateString);
      if (fixStopDate) {
        date = this.subtractOneDayFromDate(date);
      }
      date.setHours(0, 0, 0, 0);
      return this.formatDate(date);
    },
    /**
     * Set dates from defaults (while ignoring time).
     */
    setInitialDates() {
      // We change the initial value from null (see mixins/timing.js) to
      // undefined, as a workaround so that the rules kick in immediately.
      this.startDate = undefined;
      this.stopDate = undefined;

      if (this.campaign.default_startdate) {
        this.startDate = this.normalizeDateForPicker(
          this.campaign.default_startdate
        );
      }

      if (this.campaign.default_stopdate) {
        const fixStopDate = true;
        const defaultStopDate = this.normalizeDateForPicker(
          this.campaign.default_stopdate,
          fixStopDate
        );
        if (this.isNotInPast(defaultStopDate)) {
          this.stopDate = defaultStopDate;
        }
      }
    },
    /**
     * Fetch a mix of Campaign and its associated CampaignActivations.
     */
    fetchCampaignDetail(campaignId) {
      return this.$api.retrieveCampaignDetail(campaignId).then((response) => {
        this.campaignDetail = response.data;
        this.lastFetched = new Date().toISOString();
        this.updateNavbarLinks();
      });
    },
  },
};
</script>

<style scoped>
.menu-container {
  display: flex;
  align-items: baseline;
}

/**
 *  v-dialog has a z-index of 201 as of Vuetify 0.17, snackbars
 *  have one of 1000. Reduce it so the overlay is behind any
 *  fullscreen dialogs and even behind the navigation drawer (4).
 */
.fixed-snackbar-z-index {
  z-index: 5;
}

.larger-v-switch {
  transform-origin: 0 0;
  transform: scale(1.1) !important;
}

.center-aligned-switch {
  left: calc(50vw - (290px / 2));
}

.v-label--msr--text >>> label.v-label {
  color: var(--msr-color-primary) !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/CampaignDetail.vue